import { balance, type Balance } from './balance';
import { hpBasis } from './matchup';
import { DEFENSE_SLOTS, OFFENSE_SLOTS } from './playdef';
import type { AbilityEffect, Combatant, GameEvent, ItemEffect, SubWindow, Team } from './types';

/**
 * The bench (slice 9): a side's sub window between plays, and what every snap does to the bench
 * and the field after it. Pure: every function returns new teams and combatants. The game loop
 * and `playGameWith`'s preview of the offense's personnel both use `applyWindow`, so what a coach
 * sees is exactly what the engine plays. See docs/design-plan.md, "The bench" and "Healing on the
 * bench"; every number is in balance.json `hp`, `subs`, `stages`, `abilities`, and `items`.
 */

export type Side = 'offense' | 'defense';

type SubEvent = Extract<GameEvent, { type: 'sub' }>;

/** A window with nothing in it is no window: hurry-up allows only these. */
export function hasWindow(w: SubWindow<string> | undefined): boolean {
  return (w?.subs?.length ?? 0) + (w?.rest?.length ?? 0) + (w?.bag?.length ?? 0) > 0;
}

/** Roster indices in either of the team's charts: everyone not on the bench. */
export function charted(team: Team): Set<number> {
  return new Set([...Object.values(team.offense), ...Object.values(team.defense)]);
}

/** The whitelisted ability a Pokémon has, if any (balance.json `abilities`). */
export function abilityOf(c: Combatant, b: Balance): AbilityEffect | undefined {
  if (!c.abilityId) return undefined;
  return (b.abilities as unknown as Record<string, AbilityEffect | undefined>)[c.abilityId];
}

/** The held or bag item's effect and slot (balance.json `items`). */
export function itemOf(
  id: string | undefined,
  b: Balance = balance,
): { slot: string; effect: ItemEffect } | undefined {
  if (!id) return undefined;
  return (b.items as unknown as Record<string, { slot: string; effect: ItemEffect } | undefined>)[
    id
  ];
}

/** Why `applyWindow` refuses a Rest (slice 11b); the Book disables a Rest for exactly these. */
export type RestRefusal = 'on the field' | '0 HP' | 'sitting' | 'no PP' | 'no heal move';

const REST_REFUSAL_TEXT: Record<RestRefusal, string> = {
  'on the field': 'is on the field; Rest is spent on the bench',
  '0 HP': 'is at 0 HP and cannot spend moves',
  sitting: 'is already sitting out Rest',
  'no PP': 'knows no heal move with PP left',
  'no heal move': 'knows no heal move with PP left',
};

/** The first heal move (Rest, Recover) with PP left, else the first heal move known. */
export function healMove(c: Combatant): Combatant['moves'][number] | undefined {
  const heals = c.moves.filter((m) => m.effect.kind === 'heal');
  return heals.find((m) => (c.ppLeft[m.id] ?? 0) > 0) ?? heals[0];
}

/**
 * Whether roster index `i` can spend Rest on `team` as the window leaves it after its subs
 * (`on`: who is in either chart then), or why not. One rule for the engine and the Book.
 */
export function restRefusal(
  team: Team,
  i: number,
  on: ReadonlySet<number> = charted(team),
): RestRefusal | null {
  const c = team.roster[i];
  if (!c) return 'no heal move';
  if (on.has(i)) return 'on the field';
  if (c.hp <= 0) return '0 HP';
  if ((c.sitPlays ?? 0) > 0) return 'sitting';
  const move = healMove(c);
  if (!move) return 'no heal move';
  if ((c.ppLeft[move.id] ?? 0) <= 0) return 'no PP';
  return null;
}

const heal = (c: Combatant, hp: number): Combatant =>
  hp <= 0 || c.hp >= c.stats.hp ? c : { ...c, hp: Math.min(c.stats.hp, c.hp + hp) };

const cure = (c: Combatant): Combatant => {
  if (!c.status) return c;
  const out = { ...c };
  delete out.status;
  return out;
};

/**
 * One side's window on `team`: its subs in order (into the chart in play), then Rest on the bench,
 * then the bag. Anything illegal throws, as a move the Pokémon does not know does: the server
 * replays decisions and never trusts a client's.
 */
export function applyWindow(
  team: Team,
  side: Side,
  w: SubWindow<string>,
  b: Balance = balance,
): { team: Team; events: GameEvent[] } {
  const t: Team = {
    ...team,
    roster: [...team.roster],
    offense: { ...team.offense },
    defense: { ...team.defense },
    ...(team.bag ? { bag: [...team.bag] } : {}),
  };
  const events: GameEvent[] = [];
  const chart = t[side] as Record<string, number>;
  const slots: readonly string[] = side === 'offense' ? OFFENSE_SLOTS : DEFENSE_SLOTS;
  const who = `team ${t.id}`;
  const index = (i: number, what: string) => {
    if (!Number.isInteger(i) || i < 0 || i >= t.roster.length) {
      throw new Error(`${who}: ${what} ${i} is not on its roster`);
    }
    return t.roster[i] as Combatant;
  };
  const before = charted(t);

  for (const sub of w.subs ?? []) {
    if (!slots.includes(sub.slot)) throw new Error(`${who}: "${sub.slot}" is not a ${side} slot`);
    const c = index(sub.in, 'sub');
    const out = chart[sub.slot] as number;
    if (out === sub.in) continue;
    const at = slots.find((s) => chart[s] === sub.in);
    if (at !== undefined) {
      // Already on the field in this chart: the two trade slots, nobody comes in.
      chart[at] = out;
      chart[sub.slot] = sub.in;
      events.push({
        type: 'sub',
        team: t.id,
        side,
        slot: sub.slot as SubEvent['slot'],
        in: sub.in,
        out,
        swap: true,
        from: at as SubEvent['slot'],
      });
      continue;
    }
    if ((c.sitPlays ?? 0) > 0) {
      throw new Error(`${who}: ${c.name} sits ${c.sitPlays} more plays after Rest`);
    }
    chart[sub.slot] = sub.in;
    t.roster[sub.in] = {
      ...c,
      enteredThisPlay: true,
      ...(b.stages.travelOnSub ? {} : { stages: {} }),
    };
    events.push({
      type: 'sub',
      team: t.id,
      side,
      slot: sub.slot as SubEvent['slot'],
      in: sub.in,
      out,
    });
  }

  // Benched: in a chart before the window, in neither after it.
  const after = charted(t);
  for (const i of before) {
    if (after.has(i)) continue;
    let c = t.roster[i] as Combatant;
    if (!b.stages.travelOnSub && Object.keys(c.stages).length > 0) c = { ...c, stages: {} };
    const ability = abilityOf(c, b);
    const last = [...events]
      .reverse()
      .find((e): e is SubEvent => e.type === 'sub' && e.out === i && !e.swap);
    if (ability?.kind === 'bench-heal') {
      const healed = heal(c, Math.round(c.stats.hp * ability.fraction));
      if (healed.hp !== c.hp && last) last.regenerated = healed.hp - c.hp;
      c = healed;
    }
    if (ability?.kind === 'bench-cure' && c.status) {
      c = cure(c);
      if (last) last.cured = true;
    }
    t.roster[i] = c;
  }

  for (const i of w.rest ?? []) {
    const c = index(i, 'Rest');
    const refused = restRefusal(t, i, after);
    if (refused) throw new Error(`${who}: ${c.name} ${REST_REFUSAL_TEXT[refused]}`);
    const move = healMove(c);
    if (move?.effect.kind !== 'heal') throw new Error(`${who}: ${c.name} has no heal move`);
    const { fraction, sitPlays } = move.effect;
    let next = heal(
      { ...c, ppLeft: { ...c.ppLeft, [move.id]: (c.ppLeft[move.id] ?? 0) - 1 } },
      Math.round(hpBasis(c.stats.hp, b) * fraction),
    );
    if (sitPlays > 0) next = { ...cure(next), sitPlays };
    t.roster[i] = next;
    events.push({ type: 'rest', team: t.id, target: i, moveId: move.id, hp: next.hp, sitPlays });
  }

  for (const use of w.bag ?? []) {
    const c = index(use.target, 'bag target');
    const k = t.bag?.indexOf(use.item) ?? -1;
    if (k < 0 || !t.bag) throw new Error(`${who} has no ${use.item} left in its bag`);
    const item = itemOf(use.item, b);
    if (item?.slot !== 'bag') throw new Error(`${who}: ${use.item} is not a bag item`);
    t.bag.splice(k, 1);
    let next = c;
    if (item.effect.kind === 'heal-hp') next = heal(c, item.effect.hp);
    if (item.effect.kind === 'cure-status') next = cure(c);
    t.roster[use.target] = next;
    events.push({
      type: 'bag',
      team: t.id,
      item: use.item,
      target: use.target,
      hp: next.hp,
      cured: !!c.status && !next.status,
    });
  }
  return { team: t, events };
}

/**
 * What a snap does to one Pokémon after it: status counts down (on the bench too, by
 * `status.countsDownOnBench`); on the field, Leftovers heals and the first-snap flag clears; on the
 * bench, it heals `hp.benchHealPerPlay` of max (of the reference max in "points", hpBasis) and one
 * play of Rest is sat. Never above max HP.
 */
export function afterSnap(c: Combatant, onField: boolean, b: Balance): Combatant {
  let out = c;
  if (out.status && (onField || b.status.countsDownOnBench)) {
    const left = out.status.playsLeft - 1;
    out = left > 0 ? { ...out, status: { ...out.status, playsLeft: left } } : cure(out);
  }
  if (onField) {
    const held = itemOf(out.itemId, b);
    if (held?.effect.kind === 'heal-per-play') {
      out = heal(out, Math.round(out.stats.hp * held.effect.fraction));
    }
    if (out.enteredThisPlay) out = { ...out, enteredThisPlay: false };
    return out;
  }
  out = heal(out, Math.round(hpBasis(out.stats.hp, b) * b.hp.benchHealPerPlay));
  if ((out.sitPlays ?? 0) > 0) {
    const left = (out.sitPlays as number) - 1;
    if (left > 0) out = { ...out, sitPlays: left };
    else {
      out = { ...out };
      delete out.sitPlays;
    }
  }
  return out;
}
